import { fetchEntity, fetchEntityList } from '@/lib/entity-api';
import { getApiUrl } from '@/lib/api-config';
import type { CellData, ManualComparison, ManualComparisonSummary, SaveComparisonData, TableColumn } from '../types';

/**
 * Comparison Sheets. A sheet is a `bid_comparison` (the parent: name, project, and the column / item axes) plus one
 * `bid_comparison_manual` child PER COLUMN (a vendor), whose `data` holds only that column's values:
 * { column_id, total, cells: { [itemId]: CellData } }.
 *
 * Every read and write goes through the backend's generic entity API (company, author and ownership are the server's);
 * a sheet's columns are read by `filters[comparison_id]` and written in one bulk request.
 */
const PARENT = 'bid_comparison';
const CHILD = 'bid_comparison_manual';

const EMPTY_CELL: CellData = { value: '', type: 'text', checked: false };

async function send<T = any>(path: string, method: 'POST' | 'PUT' | 'DELETE', body: unknown, failure: string): Promise<T> {
  const response = await fetch(getApiUrl(path), {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  });
  const parsed = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(parsed.error || parsed.message || failure);
  return parsed.data as T;
}

/** The sheet's axes as stored on the parent: columns by id, items by position. */
function toAxes(columns: TableColumn[], rows: (string | CellData)[][]) {
  return {
    columnNames: columns.map((col) => ({ id: col.id, subcontractorName: col.subcontractorName })),
    itemNames: rows.map((row, idx) => ({ id: `row-${idx}`, itemName: typeof row[0] === 'string' ? row[0] : `Item ${idx + 1}` })),
  };
}

/** One child per column: that column's total and its cell for every item. */
function toColumnRows(comparisonId: string, columns: TableColumn[], itemNames: { id: string }[], rows: (string | CellData)[][]) {
  return columns.map((col, colIdx) => {
    const cells: Record<string, CellData> = {};
    rows.forEach((row, rowIdx) => {
      const itemId = itemNames[rowIdx]?.id;
      if (!itemId) return;
      const raw = row[colIdx + 1]; // row[0] is the item name
      cells[itemId] = typeof raw === 'string' ? { value: raw, type: 'text', checked: false } : (raw as CellData) || EMPTY_CELL;
    });
    return { comparison_id: comparisonId, data: { column_id: col.id, total: col.total || '', cells } };
  });
}

const sortedByCreation = (rows: any[]) => [...rows].sort((a, b) => String(a.created_at).localeCompare(String(b.created_at)));

export const manualComparisonService = {
  /** The company's saved sheets (of one project, or all of them), newest first. */
  async getComparisons(projectId?: string): Promise<ManualComparisonSummary[]> {
    const parents = await fetchEntityList<any>(PARENT, { projectId, includeCreator: true });
    return parents.map((row) => ({
      id: row.id,
      name: row.name,
      project_id: row.project_id,
      project_name: row.project?.name ?? '',
      created_at: row.created_at,
      created_by_name: row.created_by_name ?? '',
      columnCount: Array.isArray(row.column_names) ? row.column_names.length : 0,
      itemCount: Array.isArray(row.item_names) ? row.item_names.length : 0,
    }));
  },

  /** One saved sheet, reassembled into the shape the editor works with (null when it no longer exists). */
  async getComparison(id: string): Promise<ManualComparison | null> {
    let parent: any;
    try {
      parent = await fetchEntity<any>(PARENT, id);
    } catch {
      return null;
    }
    if (!parent) return null;
    const children = sortedByCreation(await fetchEntityList<any>(CHILD, { filters: { comparison_id: id } }));

    const columnNames: TableColumn[] = Array.isArray(parent.column_names) ? parent.column_names : [];
    const itemNames: { id: string; itemName: string }[] = Array.isArray(parent.item_names) ? parent.item_names : [];
    // a column saved twice (an interrupted update) keeps its latest values: later rows overwrite earlier ones
    const childByColumnId = new Map<string, any>();
    children.forEach((child) => {
      const columnId = child.data?.column_id;
      if (columnId) childByColumnId.set(columnId, child);
    });

    const columns: TableColumn[] = columnNames.map((col) => ({
      id: col.id,
      subcontractorName: col.subcontractorName,
      total: childByColumnId.get(col.id)?.data?.total || '',
    }));
    const rows: (string | CellData)[][] = itemNames.map((item) => [
      item.itemName,
      ...columnNames.map((col) => childByColumnId.get(col.id)?.data?.cells?.[item.id] || EMPTY_CELL),
    ]);

    return {
      id: parent.id,
      comparison_id: parent.id,
      created_at: parent.created_at,
      data: { name: parent.name, project_id: parent.project_id, columns, rows, notes: parent.description || '' },
    };
  },

  async createComparison(data: SaveComparisonData): Promise<{ id: string; name: string; project_id: string }> {
    if (!data.project_id) throw new Error('Select a project before saving a Comparison Sheet.');
    const { columnNames, itemNames } = toAxes(data.columns, data.rows);
    const parent = await send<{ id: string }>(`api/entities/${PARENT}`, 'POST', {
      project_id: data.project_id,
      name: data.name,
      column_names: columnNames,
      item_names: itemNames,
      description: data.notes || '',
    }, 'Failed to save the Comparison Sheet');

    try {
      await send(`api/entities/${CHILD}/bulk`, 'POST', { items: toColumnRows(parent.id, data.columns, itemNames, data.rows) }, 'Failed to save the Comparison Sheet columns');
    } catch (error) {
      // never leave a sheet without its columns behind
      await send(`api/entities/${PARENT}/${parent.id}`, 'DELETE', {}, '').catch(() => undefined);
      throw error;
    }
    return { id: parent.id, name: data.name, project_id: data.project_id };
  },

  async updateComparison(id: string, data: Omit<SaveComparisonData, 'project_id'>): Promise<{ id: string; name: string }> {
    const { columnNames, itemNames } = toAxes(data.columns, data.rows);
    // The sheet's axes and its columns are independent writes: both at once. The server replaces the columns in a
    // single request (new ones written before the old ones are removed).
    await Promise.all([
      send(`api/entities/${PARENT}/${id}`, 'PUT', {
        name: data.name,
        column_names: columnNames,
        item_names: itemNames,
        description: data.notes || '',
      }, 'Failed to save the Comparison Sheet'),
      send(`api/entities/${CHILD}/bulk/${id}`, 'PUT', { items: toColumnRows(id, data.columns, itemNames, data.rows) }, 'Failed to save the Comparison Sheet columns'),
    ]);
    return { id, name: data.name };
  },

  async deleteComparison(id: string): Promise<void> {
    await send(`api/entities/${CHILD}/bulk/${id}`, 'DELETE', {}, 'Failed to delete the Comparison Sheet');
    await send(`api/entities/${PARENT}/${id}`, 'DELETE', {}, 'Failed to delete the Comparison Sheet');
  },
};
